import {
  Button,
  Checkbox,
  Heading,
  Label,
  Page,
  TextField,
  InlineError,
  DateTimePicker
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import {
  emailSyncContentConstraints,
  validateForm,
  preFormatForm,
  toast,
  getTranslatedText
} from '@nile/components';
import { RESTORE_REQUESTER_EMAILS } from '@nile/graphql/src/Query/ticket';
import { TicketClient } from '@nile/graphql/src';
import { useMutation } from '@apollo/client';
import { injectIntl } from 'react-intl';

const DatePickerWrapper = styled.div`
  .react-datepicker__input-container > input {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    padding: 10px;
    width: 250px;
  }
`;

function EmailContentSync({ intl }) {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    host: null,
    port: null,
    userName: null,
    password: null
  });
  const [formData, setFormData] = useState({
    host: null,
    port: null,
    requireSSL: false,
    userName: null,
    password: null,
    startDate: null
  });

  const onChangeValue = (key, value) => {
    setFormData({
      ...formData,
      [key]: value
    });
  };

  const handleValidation = () => {
    const validationResult = validateForm(
      preFormatForm(formData),
      emailSyncContentConstraints
    );

    return validationResult;
  };

  const [requestRecoverEmail, { loading }] = useMutation(
    RESTORE_REQUESTER_EMAILS,
    {
      client: TicketClient,
      onCompleted(res) {
        if (res) {
          toast('success', 'temp.email.success.message');
        } else {
          toast('error', 'temp.email.failure.message');
        }
      }
    }
  );

  const handleSave = () => {
    const validationResult = handleValidation();

    if (validationResult) {
      setErrors(validationResult);
    } else {
      setErrors({});
      requestRecoverEmail({
        variables: {
          input: formData
        }
      });
    }
  };

  return (
    <div className="full-height mt-20 mb-20">
      <Page
        title={getTranslatedText(intl, 'temp.email.sync.contents')}
        contentHeight="120px"
        variant="primary"
      >
        <Heading element="h2">
          {getTranslatedText(intl, 'temp.sync.email.incoming')}
        </Heading>
        <div className="mb-40">
          <div className="mt-20 mb-10">
            <Heading element="h4">
              {getTranslatedText(intl, 'temp.email.server.configuration')}
            </Heading>
          </div>
          <DatePickerWrapper className="mb-20">
            <div className="mb5">
              <Label>{getTranslatedText(intl, 'common.startDate')}</Label>
            </div>
            <DateTimePicker
              dateFormat="yyyy-MM-dd"
              selected={formData.startDate}
              onChange={(date: Date) => onChangeValue('startDate', date)}
              placeholderText="YYYY-MM-DD"
            />
            {errors.startDate && (
              <InlineError message={errors.startDate[0]} fieldID="encryption" />
            )}
          </DatePickerWrapper>
          <div className="fx gap-20 mb-10">
            <div className="width-250">
              <TextField
                label={getTranslatedText(intl, 'common.server')}
                variant="outline"
                value={formData.host}
                placeholder="mail.example.com"
                error={errors.host && errors.host[0]}
                onChange={(value) => {
                  onChangeValue('host', value);
                }}
              />
            </div>
            <div className="width-150">
              <TextField
                label={getTranslatedText(intl, 'common.port')}
                variant="outline"
                placeholder="447"
                value={formData.port}
                error={errors.port && errors.port[0]}
                onChange={(value) => {
                  onChangeValue('port', value);
                }}
              />
            </div>
          </div>
          <div className="width-150">
            <Checkbox
              label={getTranslatedText(intl, 'common.use.sll')}
              checked={formData.requireSSL}
              onChange={(value) => {
                onChangeValue('requireSSL', value);
              }}
            />
            {errors.requireSSL && (
              <InlineError
                message={errors.requireSSL[0]}
                fieldID="encryption"
              />
            )}
          </div>
        </div>
        <div className="mb-20">
          <div className="mt-20 mb-10">
            <Heading element="h4">
              {getTranslatedText(intl, 'common.authentication')}
            </Heading>
          </div>
          <div className="mb-10">
            <TextField
              label={getTranslatedText(intl, 'communityScript.userName')}
              variant="outline"
              size="small"
              error={errors.userName && errors.userName[0]}
              value={formData.userName}
              placeholder="demo@example.com"
              onChange={(value) => {
                onChangeValue('userName', value);
              }}
            />
          </div>
          <div className="mb-10">
            <TextField
              label={getTranslatedText(
                intl,
                'attribute.Itdoc.password.password'
              )}
              variant="outline"
              error={errors.password && errors.password[0]}
              type={showPassword ? 'text' : 'password'}
              size="small"
              value={formData.password}
              onChange={(value) => {
                onChangeValue('password', value);
              }}
              suffix={
                <span
                  className="position-absolute r5 cursor-pointer z99"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <SVGIcon name={showPassword ? 'EyeVisible' : 'EyeHidden'} />
                </span>
              }
            />
          </div>
        </div>
        <Button primary onClick={handleSave} loading={loading}>
          {getTranslatedText(intl, 'admin.integrations.connect')}
        </Button>
      </Page>
    </div>
  );
}

export default injectIntl(EmailContentSync);
